import React, { ReactNode, useRef } from 'react';
import styles from './SegmentedControl.module.css';

export interface SegmentedControlOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  ariaLabel,
}) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIndex = options.findIndex((o) => o.value === value);

  const focusAndSelect = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    buttonRefs.current[index]?.focus();
    if (option.value !== value) {
      onChange(option.value);
    }
  };

  const nextEnabledIndex = (start: number, direction: 1 | -1): number => {
    const n = options.length;
    for (let step = 1; step <= n; step++) {
      const idx = ((start + direction * step) % n + n) % n;
      if (!options[idx].disabled) return idx;
    }
    return start;
  };

  const firstEnabledIndex = (): number => {
    const idx = options.findIndex((o) => !o.disabled);
    return idx === -1 ? 0 : idx;
  };

  const lastEnabledIndex = (): number => {
    for (let i = options.length - 1; i >= 0; i--) {
      if (!options[i].disabled) return i;
    }
    return options.length - 1;
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusAndSelect(nextEnabledIndex(index, 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusAndSelect(nextEnabledIndex(index, -1));
        break;
      case 'Home':
        e.preventDefault();
        focusAndSelect(firstEnabledIndex());
        break;
      case 'End':
        e.preventDefault();
        focusAndSelect(lastEnabledIndex());
        break;
      default:
        break;
    }
  };

  const indicatorStyle: React.CSSProperties = {
    '--sc-active-index': activeIndex >= 0 ? activeIndex : 0,
    '--sc-count': options.length,
  } as React.CSSProperties;

  // When `value` doesn't match any option, keep the control tab-reachable by
  // putting tabIndex=0 on the first enabled segment instead of -1 everywhere.
  const fallbackTabIndex = activeIndex < 0 ? firstEnabledIndex() : -1;

  return (
    <div
      className={styles.track}
      role="radiogroup"
      aria-label={ariaLabel}
      style={indicatorStyle}
    >
      {activeIndex >= 0 && <span className={styles.indicator} aria-hidden="true" />}
      {options.map((option, index) => {
        const isActive = option.value === value;
        const tabIndex = isActive ? 0 : index === fallbackTabIndex ? 0 : -1;
        return (
          <button
            key={option.value}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            tabIndex={tabIndex}
            disabled={option.disabled}
            className={`${styles.segment} ${isActive ? styles.segmentActive : ''} ${option.disabled ? styles.segmentDisabled : ''}`}
            onClick={() => {
              if (option.disabled) return;
              if (option.value !== value) onChange(option.value);
            }}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {option.icon != null && <span className={styles.icon}>{option.icon}</span>}
            <span className={styles.label}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
